import React from 'react';
import { Autocomplete, Box, SxProps, Theme } from '@mui/material';
import { InputField } from '@/pages/components/common/InputField';

export interface ColumnOption {
    key: string;
    label: string;
}

/** DR/CR 多行时科目与金额共用的行高，保证左右对齐 */
export const DR_CR_LINE_HEIGHT = 40;

interface ColumnSearchSelectProps {
    options: ColumnOption[];
    value: string | null;
    onChange: (key: string | null) => void;
    placeholder?: string;
    /** 默认加宽，便于看科目名 */
    width?: number;
    sx?: SxProps<Theme>;
    /** 透传到输入框，便于编辑行键盘流 */
    onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    autoFocus?: boolean;
}

const alignInputSx = {
    width: '100%',
    '& .MuiFormControl-root': { width: '100%' },
    '& .MuiTextField-root': { width: '100%' },
    '& .MuiInputBase-root': {
        width: '100%',
        height: 'auto',
        minHeight: DR_CR_LINE_HEIGHT - 4,
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        paddingTop: 0,
        paddingBottom: 0,
    },
    '& .MuiAutocomplete-inputRoot': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '0 !important',
        width: '100%',
    },
    '& .MuiAutocomplete-input': {
        minWidth: '0 !important',
        width: '100% !important',
        paddingTop: '4px !important',
        paddingBottom: '4px !important',
        paddingLeft: '14px !important',
        paddingRight: '14px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        right: 4,
        top: '50%',
        transform: 'translateY(-50%)',
        height: 'auto',
        maxHeight: '100%',
    },
    '& .MuiOutlinedInput-adornedEnd': {
        paddingRight: 28,
    },
    '& .MuiInputBase-input': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
} as const;

/** DR/CR 科目选择：按英文、中文、科目代码过滤。 */
export function ColumnSearchSelect({
    options,
    value,
    onChange,
    placeholder = 'Search account',
    width = 260,
    sx,
    onInputKeyDown,
    autoFocus,
}: ColumnSearchSelectProps) {
    const selected = options.find((option) => option.key === value) ?? null;
    const fieldSx = {
        minWidth: 0,
        ...alignInputSx,
        ...((sx as object) || {}),
    } as const;

    return (
        <Box sx={{ width, minWidth: width, maxWidth: width, flexShrink: 0 }}>
            <Autocomplete
                size="small"
                options={options}
                value={selected}
                onChange={(_, option) => onChange(option?.key ?? null)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, current) => option.key === current.key}
                openOnFocus
                autoHighlight
                fullWidth
                slotProps={{
                    popper: {
                        sx: {
                            width: 'auto !important',
                            minWidth: Math.max(width, 320),
                            maxWidth: 520,
                        },
                    },
                    listbox: {
                        sx: {
                            '& .MuiAutocomplete-option': {
                                whiteSpace: 'nowrap',
                                fontSize: 13,
                            },
                        },
                    },
                }}
                sx={fieldSx}
                renderInput={(params) => (
                    <InputField
                        {...params}
                        margin="none"
                        fullWidth
                        autoFocus={autoFocus}
                        placeholder={selected ? undefined : placeholder}
                        title={selected?.label}
                        sx={{ ...fieldSx, minWidth: 0, width: '100%' }}
                        inputProps={{
                            ...params.inputProps,
                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                const prev = params.inputProps?.onKeyDown as
                                    | React.KeyboardEventHandler<HTMLInputElement>
                                    | undefined;
                                prev?.(e);
                                if (!e.defaultPrevented) onInputKeyDown?.(e);
                            },
                        }}
                        InputProps={{ ...params.InputProps }}
                    />
                )}
            />
        </Box>
    );
}
